import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Upload, User as UserIcon, Loader2, Trash2 } from 'lucide-react';
import { BrutalButton } from './ui/BrutalButton';
import { BrutalInput } from './ui/BrutalInput';
import { Label } from './ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface TeamMember {
  name: string;
  role_it?: string;
  role_en?: string;
  role_es?: string;
  photo_url?: string;
  linkedin_url?: string;
}

interface TeamMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (member: TeamMember) => void;
  editingMember?: TeamMember | null;
  userId: string;
}

const TeamMemberModal = ({ isOpen, onClose, onSave, editingMember, userId }: TeamMemberModalProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [memberData, setMemberData] = useState<TeamMember>({
    name: '',
    role_it: '',
    role_en: '',
    role_es: '',
    photo_url: '',
    linkedin_url: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (editingMember) {
      setMemberData(editingMember);
    } else {
      setMemberData({
        name: '',
        role_it: '',
        role_en: '',
        role_es: '',
        photo_url: '',
        linkedin_url: ''
      });
    }
  }, [editingMember, isOpen]);

  const validateFile = (file: File): boolean => {
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!validTypes.includes(file.type)) {
      toast({
        title: 'Formato non valido',
        description: 'Carica un file JPG, PNG o WebP',
        variant: 'error'
      });
      return false;
    }

    if (file.size > maxSize) {
      toast({
        title: 'File troppo grande',
        description: 'La dimensione massima è 5MB',
        variant: 'error'
      });
      return false;
    }

    return true;
  };

  const handlePhotoUpload = async (file: File) => {
    if (!validateFile(file)) return;

    setIsUploading(true);
    try {
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      const fileName = `team-member-${Date.now()}.${fileExt}`;
      const filePath = `${userId}/team-members/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('profile-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('profile-images')
        .getPublicUrl(filePath);

      setMemberData(prev => ({ ...prev, photo_url: publicUrl }));
      
      toast({
        title: 'Foto caricata',
        description: 'La foto è stata caricata con successo',
        variant: 'success'
      });
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Errore upload',
        description: 'Impossibile caricare la foto. Riprova.',
        variant: 'error'
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handlePhotoUpload(file);
  };

  const handleRemovePhoto = () => {
    setMemberData(prev => ({ ...prev, photo_url: '' }));
  };

  const validateInput = () => {
    const newErrors: Record<string, string> = {};
    
    // Nome obbligatorio
    if (!memberData.name.trim()) {
      newErrors.name = t('profile.edit.errors.nameRequired');
    } else if (memberData.name.length > 100) {
      newErrors.name = 'Nome troppo lungo (max 100 caratteri)';
    }
    
    // Almeno un ruolo richiesto
    if (!memberData.role_it && !memberData.role_en && !memberData.role_es) {
      newErrors.role = 'Inserisci almeno un ruolo in una lingua';
    }
    
    // Validazione LinkedIn URL (se presente)
    if (memberData.linkedin_url && !/^https?:\/\/(www\.)?linkedin\.com\//.test(memberData.linkedin_url)) {
      newErrors.linkedin_url = 'URL LinkedIn non valido';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateInput()) {
      return;
    }
    
    // Sanitize input
    const sanitizedData = {
      ...memberData,
      name: memberData.name.trim(),
      role_it: memberData.role_it?.trim() || '',
      role_en: memberData.role_en?.trim() || '',
      role_es: memberData.role_es?.trim() || '',
      linkedin_url: memberData.linkedin_url?.trim() || '',
    };
    
    onSave(sanitizedData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-surface border-[3px] border-dark shadow-[8px_8px_0px_#101322] max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-primary border-b-[3px] border-dark p-4 flex justify-between items-center">
          <h2 className="font-heading text-xl font-bold">
            {editingMember ? t('profile.edit.fields.editTeamMember') : t('profile.edit.fields.addTeamMember')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-dark/10 transition-colors rounded"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Foto */}
          <div>
            <Label>{t('profile.edit.fields.teamMemberPhoto')}</Label>
            <div className="flex items-center gap-4 mt-2">
              <div className="w-20 h-20 border-[3px] border-dark bg-muted flex items-center justify-center overflow-hidden">
                {memberData.photo_url ? (
                  <img src={memberData.photo_url} alt="Member" className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <UserIcon className="w-8 h-8 text-muted-foreground" />
                )}
              </div>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              <BrutalButton 
                variant="outline" 
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                {isUploading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                {isUploading ? 'Caricamento...' : 'Carica foto'}
              </BrutalButton>
              
              {memberData.photo_url && !isUploading && (
                <BrutalButton 
                  variant="outline" 
                  size="sm"
                  onClick={handleRemovePhoto}
                >
                  <Trash2 className="w-4 h-4" />
                </BrutalButton>
              )}
            </div>
          </div>

          {/* Nome */}
          <div>
            <Label htmlFor="member-name">{t('profile.edit.fields.teamMemberName')} *</Label>
            <BrutalInput
              id="member-name"
              value={memberData.name}
              onChange={(e) => {
                setMemberData({ ...memberData, name: e.target.value });
                if (errors.name) setErrors({ ...errors, name: '' });
              }}
              placeholder="Mario Rossi"
              maxLength={100}
              required
            />
            {errors.name && <p className="text-destructive text-sm mt-1">{errors.name}</p>}
          </div>

          {/* Ruolo multilingua */}
          <div>
            <Label>{t('profile.edit.fields.teamMemberRole')} *</Label>
            <div className="space-y-3 mt-2">
              <div>
                <label className="text-xs text-muted-foreground">🇮🇹 Italiano</label>
                <BrutalInput
                  value={memberData.role_it || ''}
                  onChange={(e) => {
                    setMemberData({ ...memberData, role_it: e.target.value });
                    if (errors.role) setErrors({ ...errors, role: '' });
                  }}
                  placeholder="CEO, Responsabile Marketing, etc."
                  maxLength={100}
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">🇬🇧 English</label>
                <BrutalInput
                  value={memberData.role_en || ''}
                  onChange={(e) => {
                    setMemberData({ ...memberData, role_en: e.target.value });
                    if (errors.role) setErrors({ ...errors, role: '' });
                  }}
                  placeholder="CEO, Marketing Manager, etc."
                  maxLength={100}
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">🇪🇸 Español</label>
                <BrutalInput
                  value={memberData.role_es || ''}
                  onChange={(e) => {
                    setMemberData({ ...memberData, role_es: e.target.value });
                    if (errors.role) setErrors({ ...errors, role: '' });
                  }}
                  placeholder="CEO, Gerente de Marketing, etc."
                  maxLength={100}
                />
              </div>
            </div>
            {errors.role && <p className="text-destructive text-sm mt-1">{errors.role}</p>}
          </div>

          {/* LinkedIn */}
          <div>
            <Label htmlFor="member-linkedin">{t('profile.edit.fields.teamMemberLinkedin')}</Label>
            <BrutalInput
              id="member-linkedin"
              type="url"
              value={memberData.linkedin_url || ''}
              onChange={(e) => {
                setMemberData({ ...memberData, linkedin_url: e.target.value });
                if (errors.linkedin_url) setErrors({ ...errors, linkedin_url: '' });
              }}
              placeholder="https://linkedin.com/in/nome"
            />
            {errors.linkedin_url && <p className="text-destructive text-sm mt-1">{errors.linkedin_url}</p>}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-surface border-t-[3px] border-dark p-4 flex justify-end gap-3">
          <BrutalButton variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </BrutalButton>
          <BrutalButton 
            variant="primary" 
            onClick={handleSave}
            disabled={!memberData.name.trim()}
          >
            {t('common.save')}
          </BrutalButton>
        </div>
      </div>
    </div>
  );
};

export default TeamMemberModal;
